import { Box, HStack, Tooltip } from '@chakra-ui/react';

const PARTBAY_BLUE = '#0071E3';

/* ────────────────────────────────────────────────────────────
 *  SETUP-PHASE stepper
 *  Organization → Verify → Account
 * ──────────────────────────────────────────────────────────── */

const SETUP_LABELS = ['Organization', 'Verify', 'Account'] as const;

const SETUP_STEP_MAP: Record<string, number> = {
	'exists': 0,
	'create-org': 0,
	'verify-org': 1,
	'account': 2,
};

/* ────────────────────────────────────────────────────────────
 *  ONBOARDING-PHASE stepper
 *  Account Setup · Logins · Locations · Platform Logins · IT Setup · Suppliers · Users · Preferences
 * ──────────────────────────────────────────────────────────── */

const ONBOARDING_LABELS = [
	'Account Setup',
	'Logins',
	'Locations',
	'Platform Logins',
	'IT Setup',
	'Suppliers',
	'Users',
	'Preferences',
] as const;

const ONBOARDING_STEP_MAP: Record<string, number> = {
	'exists': 0,
	'create-org': 0,
	'verify-org': 0,
	'account': 0,
	'dependency-setup': 1,
	'locations': 2,
	'mitchell-logins': 3,
	'ops-logins': 3,
	'it-setup': 4,
	'supplier-network': 5,
	'invite-users': 6,
	'org-settings': 7,
};

export type CreateStep =
	'exists'
	| 'create-org'
	| 'verify-org'
	| 'account'
	| 'dependency-setup'
	| 'locations'
	| 'it-setup'
	| 'mitchell-logins'
	| 'ops-logins'
	| 'supplier-network'
	| 'invite-users'
	| 'org-settings';

/** Maps an onboarding stepper index back to its first CreateStep (for click-to-go-back). */
const ONBOARDING_INDEX_TO_STEP: CreateStep[] = [
	'exists',            // 0 — Account Setup
	'dependency-setup',  // 1 — Logins
	'locations',         // 2 — Locations
	'mitchell-logins',   // 3 — Platform Logins
	'it-setup',          // 4 — IT Setup
	'supplier-network',  // 5 — Suppliers
	'invite-users',      // 6 — Users
	'org-settings',      // 7 — Preferences
];

/** Returns the display label for a setup-phase step (used in the page header). */
export const getSetupStepLabel = (step: CreateStep): string => {
	const index = SETUP_STEP_MAP[step] ?? 0;
	return SETUP_LABELS[index];
};

/** Returns the display label for an onboarding-phase step (used in the page header). */
export const getOnboardingStepLabel = (step: CreateStep): string => {
	const index = ONBOARDING_STEP_MAP[step] ?? 0;
	return ONBOARDING_LABELS[index];
};

/* ─── Shared slim segmented progress bar ─── */

interface SlimProgressBarProps {
	labels: readonly string[];
	currentIndex: number;
	onSegmentClick?: (index: number) => void;
}

const SlimProgressBar = ({ labels, currentIndex, onSegmentClick }: SlimProgressBarProps) => (
	<HStack spacing={1.5} w="full" align="center">
		{labels.map((label, index) => {
			const isActive = index === currentIndex;
			const isCompleted = index < currentIndex;
			const isClickable = isCompleted && index > 0 && !!onSegmentClick;

			return (
				<Tooltip
					key={index}
					label={label}
					placement="top"
					hasArrow
					openDelay={120}
					fontSize="xs"
				>
					<Box
						flex={1}
						h="4px"
						borderRadius="full"
						bg={isCompleted || isActive ? PARTBAY_BLUE : 'gray.200'}
						cursor={isClickable ? 'pointer' : 'default'}
						onClick={isClickable ? () => onSegmentClick?.(index) : undefined}
						transition="all 0.2s ease"
						_hover={{
							transform: 'scaleY(1.6)',
							opacity: isClickable ? 0.85 : 1,
						}}
						sx={
							isActive
								? {
										animation: 'progressBarPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
										'@keyframes progressBarPulse': {
											'0%, 100%': { opacity: 1 },
											'50%': { opacity: 0.55 },
										},
									}
								: undefined
						}
					/>
				</Tooltip>
			);
		})}
	</HStack>
);

/* ─── Setup-phase bar (3 segments) ─── */

interface SetupProgressProps {
	currentStep: CreateStep;
}

export const SetupProgressBar = ({ currentStep }: SetupProgressProps) => {
	const currentIndex = SETUP_STEP_MAP[currentStep] ?? 0;
	return <SlimProgressBar labels={SETUP_LABELS} currentIndex={currentIndex} />;
};

/* ─── Onboarding-phase bar (8 segments) ─── */

interface OnboardingStepperProps {
	currentStep: CreateStep;
	onStepClick?: (step: CreateStep) => void;
}

export const SetupStepper = ({ currentStep, onStepClick }: OnboardingStepperProps) => {
	const currentIndex = ONBOARDING_STEP_MAP[currentStep] ?? 0;
	return (
		<SlimProgressBar
			labels={ONBOARDING_LABELS}
			currentIndex={currentIndex}
			onSegmentClick={onStepClick ? (index) => onStepClick(ONBOARDING_INDEX_TO_STEP[index]) : undefined}
		/>
	);
};
